<template>
    <div>
        <question class="d-flex justify-content-between">
            <div class="w-75">
                <div class="mb-1" v-html="$t(item.text + '_LINE_2', investmentAmount)"></div>
                <div v-html="$t(item.text + '_LINE_3')"></div>
            </div>
            <div>
                <div class="color-variant mb-1" style="text-align: right;" v-html="$t(item.text + '_LINE_1', rounds)"></div>
                <div class="context-menu__link" style="text-align: right;" @click="showHelpModal" role="button">
                    <b-icon-info-circle />
                    <span class="ml-2" v-html="$t(`${this.item.text}_LINE_3_HELP`)"/>
                </div>
            </div>
        </question>
        <b-row
            v-if="finalValues && differences"
            class="outcome-task__buttons d-flex mt-5 mb-4"
            :class="swap ? 'flex-row-reverse' : ''"
        >
            <b-col cols="6" lg="5" :class="swap ? 'ml-lg-4' : 'mr-lg-4'">
                <vertical-button
                    class="outcome-task__button"
                    variant="bq-burgundy"
                    :header="$t(buttonHeader(OUTCOME_TASK_RESULTS.RISK))"
                    :choice="OUTCOME_TASK_RESULTS.RISK"
                    :selected="selectedChoice === OUTCOME_TASK_RESULTS.RISK"
                    :shadowed="selectedChoice && selectedChoice !== OUTCOME_TASK_RESULTS.RISK"
                    @select="handleSelect"
                >
                    <template v-slot:option-a>
                        <outcome
                            :finalValueText="$t('INTERACTION_BASE_EITHER')"
                            :finalValue="`${locale.currency} ${formatNumber(finalValues.risk.up, locale.tag)}`"
                            :difference="`${locale.currency} ${formatNumber(differences.risk.up, locale.tag)}`"
                            :resize="selectedChoice"
                            direction="up"
                        />
                    </template>
                    <template v-slot:option-b>
                        <outcome
                            :finalValueText="$t('INTERACTION_BASE_OR')"
                            :finalValue="`${locale.currency} ${formatNumber(finalValues.risk.down, locale.tag)}`"
                            :difference="`${locale.currency} ${formatNumber(differences.risk.down * -1, locale.tag)}`"
                            :resize="selectedChoice"
                            direction="down"
                        />
                    </template>
                </vertical-button>
            </b-col>
            <b-col cols="6" lg="5" :class="swap ? 'mr-lg-4' : 'ml-lg-4'">
                <vertical-button
                    class="outcome-task__button"
                    variant="bq-turquoise"
                    :header="$t(buttonHeader(OUTCOME_TASK_RESULTS.SAFE))"
                    :choice="OUTCOME_TASK_RESULTS.SAFE"
                    :selected="selectedChoice === OUTCOME_TASK_RESULTS.SAFE"
                    :shadowed="selectedChoice && selectedChoice !== OUTCOME_TASK_RESULTS.SAFE"
                    @select="handleSelect"
                >
                    <template v-slot:option-a v-if="differences.safe > 0">
                        <outcome
                            :finalValueText="$t('INTERACTION_BASE_FIXED')"
                            :finalValue="`${locale.currency} ${formatNumber(finalValues.safe, locale.tag)}`"
                            :difference="`${locale.currency} ${formatNumber(differences.safe, locale.tag)}`"
                            :resize="selectedChoice"
                            direction="up"
                        />
                    </template>
                    <template v-slot:option-b v-if="differences.safe <= 0">
                        <outcome
                            :finalValueText="$t('INTERACTION_BASE_FIXED')"
                            :finalValue="`${locale.currency} ${formatNumber(finalValues.safe, locale.tag)}`"
                            :difference="`${locale.currency} ${formatNumber(differences.safe * -1, locale.tag)}`"
                            :resize="selectedChoice"
                            direction="down"
                        />
                    </template>
                </vertical-button>
            </b-col>
        </b-row>
        <div 
            v-if="!selectedChoice"
            style="text-align: center; font-weight: bolder;"
            v-html="$t(`${this.item.text}_LINE_4_SELECT`)"/>
        <div 
            v-else
            style="text-align: center; font-weight: bolder;"
            v-html="$t(`${this.item.text}_LINE_4_SELECTED`)"/>
    </div>
</template>
<script>
import { mapActions } from 'vuex'
import { BIconInfoCircle } from 'bootstrap-vue'

import verticalButton from '@/components/inputs/verticalButton'
import outcome from '@/components/ui/outcome'
import question from '@/components/ui/question'

import { getLabel } from '@/utils/items'
import { formatNumber } from '@/utils/numbers'
import { OUTCOME_TASK_RESULTS } from '@/constants/items'
import { ASSETS_URL } from '@/constants/misc'

export default {
    name: 'OutcomeTaskItem',
    components: {
        question,
        'vertical-button': verticalButton,
        outcome,
        BIconInfoCircle,
    },
    props: {
        item: {
            type: Object
        },
        instance: {
            type: Object
        },
        scenario: {
            type: Object
        },
        scenarioInstance: {
            type: Object
        },
        rounds: {
            type: Object
        },
        locale: {
            type: Object
        }
    },
    data() {
        return {
            formatNumber,
            OUTCOME_TASK_RESULTS,
            selectedChoice: null,
            finalValues: null,
            differences: null,
        }
    },
    watch: {
        scenarioInstance() {
            this.setValues();
        },
    },
    computed: {
        swap() {
            return this.scenarioInstance.round % 2 === 0
        },
        investmentAmount() {
            return { investmentAmount: `${this.locale.currency} ${formatNumber(this.scenario.initValue, this.locale.tag)}` }
        }
    },
    methods: {
        ...mapActions(['showDialog']),
        showHelpModal() {
            console.log(this.investmentAmount.investmentAmount);
            this.showDialog({
                modal: false,
                title: this.$t(`${this.item.text}_HELP_TITLE`),
                content: this.$t(`${this.item.text}_HELP_TEXT`,  {investmentAmount: this.investmentAmount.investmentAmount, ASSETS_URL }),
                okTitle: this.$t(`${this.item.text}_HELP_CLOSE_BUTTON`),
                onConfirm: () => {}
            })
        },
        handleSelect(choice) {
            this.selectedChoice = choice
            this.$emit('select', choice)
        },
        buttonHeader(decision) {
            if(decision === OUTCOME_TASK_RESULTS.SAFE) {
                return getLabel(this.item, 'ANLAGE_A').text
            }
            return getLabel(this.item, 'ANLAGE_B').text
        },
        setValues() {
            this.finalValues = {
                risk: {
                    up: Math.round(this.instance.oMaxPercent * this.scenario.initValue),
                    down: Math.round((this.instance.oMinPercent * this.scenario.initValue)),
                },
                safe: this.scenarioInstance.newS
            };

            this.differences = {
                risk: {
                    up: this.finalValues.risk.up - this.scenario.initValue,
                    down: this.finalValues.risk.down - this.scenario.initValue,
                },
                safe: this.scenarioInstance.newS - this.scenario.initValue
            };

            this.selectedChoice = null;
        }
    },
    mounted() {
        this.setValues();
   }
}
</script>
<style lang="scss">
.outcome-task__help-link {
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }
}

.outcome-task__buttons {
    height: 300px;
    @media screen and (max-width: 450px) {
        font-size: 0.875rem;
    }

    @media screen and (min-width: 992px) {
        justify-content: center;
    }
}
.modal-content {
    border-radius: 1rem;
}
</style>